import {
  PLAYBACK_POLL_INTERVAL_MS,
  POLL_CUE_MIN_CYCLE_MS,
  POLL_CUE_STALE_INTERVALS,
  POLL_CUE_TICK_MS,
  type PollSync,
} from '@mixwire/contracts';

/**
 * Where the app is between polls, as the freshness cue reads it.
 *
 * `phase` runs 0 → 1 across one cue cycle, which is never shorter than
 * `POLL_CUE_MIN_CYCLE_MS` however fast the surface polls: arrivals inside a
 * cycle keep it alive without restarting it, so a console re-reading its
 * channel every second does not strobe. Nothing here marks the instant an
 * answer arrives: a cue that flashed on arrival would be a light blinking at a
 * room all night — a real risk to a photosensitive viewer, and saying nothing
 * the phase does not already say. `stale` is the honest failure: fetches have
 * stopped arriving.
 */
export type PollCycle = {
  readonly phase: number;
  readonly stale: boolean;
  /** The cadence this surface settles at — what the cue's tooltip quotes. */
  readonly intervalMs: number;
};

export type PollCycleStore = {
  readonly subscribe: (onChange: () => void) => () => void;
  readonly getSnapshot: () => PollCycle;
  /**
   * Called when a fetch *resolves*, never when one starts: the cue tracks the
   * arrival of real data, and starting it on the request would have it run
   * backwards the moment the answer landed.
   */
  readonly report: (sync: PollSync, atMs: number) => void;
};

const IDLE: PollCycle = {
  phase: 0,
  stale: false,
  intervalMs: PLAYBACK_POLL_INTERVAL_MS,
};

const isSame = (a: PollCycle, b: PollCycle): boolean =>
  a.phase === b.phase && a.stale === b.stale && a.intervalMs === b.intervalMs;

/**
 * One cycle for the whole app.
 *
 * A store rather than a hook holding state, for two reasons. Every page
 * showing the cue is showing the *same* cycle, so a per-component timer would
 * be several clocks disagreeing about the same fetch. And a 100ms interval that
 * re-rendered a page's worth of components ten times a second would be paying
 * for an eight-pixel dot with the whole tree — here only what actually
 * subscribes repaints, and the interval does not run at all while nothing is
 * watching.
 */
export const createPollCycleStore = (now: () => number = Date.now): PollCycleStore => {
  const listeners = new Set<() => void>();
  const draft: {
    cycleStartedAtMs: number | undefined;
    arrivedAtMs: number | undefined;
    intervalMs: number;
    snapshot: PollCycle;
    ticker: ReturnType<typeof setInterval> | undefined;
  } = {
    cycleStartedAtMs: undefined,
    arrivedAtMs: undefined,
    intervalMs: PLAYBACK_POLL_INTERVAL_MS,
    snapshot: IDLE,
    ticker: undefined,
  };

  const cycleMs = (): number => Math.max(draft.intervalMs, POLL_CUE_MIN_CYCLE_MS);

  const measure = (): PollCycle => {
    const cycleStartedAtMs = draft.cycleStartedAtMs;
    if (cycleStartedAtMs === undefined || draft.arrivedAtMs === undefined) {
      return { ...IDLE, intervalMs: draft.intervalMs };
    }

    // Staleness is measured from the last answer and against the surface's
    // settled cadence, not against the cue's slower cycle: a console that has
    // stopped hearing back has stopped hearing back, whatever the bar is doing.
    const isStale = now() - draft.arrivedAtMs > draft.intervalMs * POLL_CUE_STALE_INTERVALS;
    const intoCycleMs = Math.max(0, now() - cycleStartedAtMs);

    return {
      phase: isStale ? 1 : Math.min(1, intoCycleMs / cycleMs()),
      stale: isStale,
      intervalMs: draft.intervalMs,
    };
  };

  /**
   * A cycle that has run out while answers were still arriving starts the next
   * one itself, rather than waiting for an arrival to land outside it. Without
   * this, a surface polling at almost exactly the cue's own cadence — the party
   * view, following the server's five-second schedule — shows an empty bar half
   * the time: an answer a few milliseconds early falls inside the cycle, so it
   * restarts nothing, and the bar sits at zero until the one after it.
   */
  const rollExpiredCycle = (): void => {
    const cycleStartedAtMs = draft.cycleStartedAtMs;
    const arrivedAtMs = draft.arrivedAtMs;
    if (cycleStartedAtMs === undefined || arrivedAtMs === undefined) {
      return;
    }

    const elapsedMs = now() - cycleStartedAtMs;
    if (elapsedMs < cycleMs() || arrivedAtMs <= cycleStartedAtMs) {
      return;
    }

    draft.cycleStartedAtMs = cycleStartedAtMs + Math.floor(elapsedMs / cycleMs()) * cycleMs();
  };

  /**
   * `getSnapshot` has to return the same value until subscribers have been
   * told otherwise, so the snapshot is computed here and read from the box —
   * measuring inside `getSnapshot` would hand React a new object on every call
   * and spin.
   */
  const publish = (): void => {
    rollExpiredCycle();
    const next = measure();
    if (isSame(draft.snapshot, next)) {
      return;
    }
    draft.snapshot = next;
    listeners.forEach((listener) => {
      listener();
    });
  };

  return {
    subscribe: (onChange): (() => void) => {
      listeners.add(onChange);
      draft.ticker ??= setInterval(publish, POLL_CUE_TICK_MS);

      return (): void => {
        listeners.delete(onChange);
        if (listeners.size === 0) {
          clearInterval(draft.ticker);
          draft.ticker = undefined;
        }
      };
    },

    getSnapshot: (): PollCycle => draft.snapshot,

    report: (sync, atMs): void => {
      draft.intervalMs = sync.intervalMs;
      draft.arrivedAtMs = atMs;
      const cycleStartedAtMs = draft.cycleStartedAtMs;
      if (cycleStartedAtMs === undefined || atMs - cycleStartedAtMs >= cycleMs()) {
        draft.cycleStartedAtMs = atMs;
      }
      publish();
    },
  };
};
